import { useEffect, useMemo, useRef, useState } from 'react';
import { AppBar, Body, PageLayout, SubMenu, SearchInput } from 'components/common';
import useBlock from 'hooks/friends/useBlock';
import FriendProfile from 'components/friends/FriendProfile';
import BlockSettingTrailingButton from 'components/friends/BlockSettingTrailingButton';
import NoList from 'components/friends/NoList';

const BLOCKED_TAB = 'blocked';
const HIDDEN_TAB = 'hidden';
type SelectedTab = typeof BLOCKED_TAB | typeof HIDDEN_TAB;
const LOADING_ID = 'PageBlockSettingLoading';

const PageBlockSetting: React.FC = () => {
  const {
    isLoading,
    blockSearchQuery,
    setBlockSearchQuery,
    hiddenSearchQuery,
    setHiddenSearchQuery,
    blockObserverRef,
    hiddenObserverRef,
    blockHasMore,
    hiddenHasMore,
    filteredBlockUserInfoList,
    filteredHiddenUserInfoList,
    handleUnblockButtonClick,
    handleUnhideButtonClick,
  } = useBlock();

  // 탭 전환
  const [selectedTab, setSelectedTab] = useState<SelectedTab>(BLOCKED_TAB);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef<Record<SelectedTab, number>>({ [BLOCKED_TAB]: 0, [HIDDEN_TAB]: 0 });

  useEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = scrollPositions.current[selectedTab]; // 스크롤 복원
  }, [selectedTab]);

  const subMenuItems = useMemo(
    () => [
      {
        key: BLOCKED_TAB,
        text: brsc.block,
        onClick: () => {
          if (scrollContainerRef.current) scrollPositions.current[selectedTab] = scrollContainerRef.current.scrollTop; // 스크롤 저장
          setSelectedTab(BLOCKED_TAB);
        },
      },
      {
        key: HIDDEN_TAB,
        text: brsc.friend_hide,
        onClick: () => {
          if (scrollContainerRef.current) scrollPositions.current[selectedTab] = scrollContainerRef.current.scrollTop; // 스크롤 저장
          setSelectedTab(HIDDEN_TAB);
        },
      },
    ],
    [selectedTab]
  );

  // 렌더링 할 요소 결정
  const searchQuery = selectedTab === BLOCKED_TAB ? blockSearchQuery : hiddenSearchQuery;
  const setSearchQuery = selectedTab === BLOCKED_TAB ? setBlockSearchQuery : setHiddenSearchQuery;
  const friendsListToShow = selectedTab === BLOCKED_TAB ? filteredBlockUserInfoList : filteredHiddenUserInfoList;
  const trailingButtonText = selectedTab === BLOCKED_TAB ? brsc.unblock : brsc.unhide;
  const trailingButtonClick = (userInfo: UserInfo) => (selectedTab === BLOCKED_TAB ? handleUnblockButtonClick(userInfo) : handleUnhideButtonClick(userInfo));
  const observerRef = selectedTab === BLOCKED_TAB ? blockObserverRef : hiddenObserverRef;
  const hasMore = selectedTab === BLOCKED_TAB ? blockHasMore : hiddenHasMore;

  // 로딩
  useEffect(() => {
    if (isLoading) BUtil.showLoading(LOADING_ID, 20000, '#BBDEFB');
    else BUtil.hideLoading(LOADING_ID);
  }, [isLoading]);

  return (
    <PageLayout>
      <AppBar title={brsc.blocked_users} />
      <Body className="flex flex-col pt-[8px]">
        <SubMenu selectedKey={selectedTab} items={subMenuItems} />
        <div ref={scrollContainerRef} className="flex flex-1 flex-col gap-[var(--spacing-2xl)] overflow-y-auto p-4">
          <SearchInput className="h-12 w-full" value={searchQuery} onChange={setSearchQuery} placeholder={brsc.nickname} />
          {friendsListToShow.length > 0 ? (
            <div className="flex flex-1 flex-col gap-[var(--spacing-2xl)]">
              {friendsListToShow.map((userInfo, _index) => (
                <FriendProfile
                  key={userInfo.fbUid}
                  userInfo={userInfo}
                  trailing={<BlockSettingTrailingButton text={trailingButtonText} onClick={() => trailingButtonClick(userInfo)} />}
                />
              ))}
              {hasMore && <div ref={observerRef} />}
            </div>
          ) : (
            <NoList />
          )}
        </div>
      </Body>
    </PageLayout>
  );
};

export default PageBlockSetting;
